// ---------------------------------------------------------------------------
// Task mutations — write to Odoo, with optimistic local updates + rollback.
// ---------------------------------------------------------------------------
// Every write returns the server's authoritative TaskDetail; we reconcile local
// state to it. Drag moves apply optimistically (instant) and roll back on error.
// ---------------------------------------------------------------------------

import { api } from '@/core/api/apiClient';
import { useTasksStore } from './store/tasksStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useFilterStore } from '@/features/filters/store/filterStore';
import { useToastStore } from '@/core/ui/toastStore';
import type { CreateTaskInput, TaskDetail, TaskListItem, UpdateTaskInput } from '@cg-projects/shared';

const CLOSED = new Set(['1_done', '1_canceled']);

function toListItem(t: TaskDetail): TaskListItem {
  const { descriptionHtml: _d, children: _c, blockedBy: _b, blocks: _bl, ...item } = t;
  return item;
}

function areaOf(taskId: number): number | undefined {
  const { tasksByArea } = useTasksStore.getState();
  for (const [aid, list] of Object.entries(tasksByArea)) {
    if (list.some((t) => t.id === taskId)) return Number(aid);
  }
  return undefined;
}

/** The task's current board row (pre-write), wherever it lives. */
function listItemOf(taskId: number): TaskListItem | undefined {
  const { tasksByArea } = useTasksStore.getState();
  for (const list of Object.values(tasksByArea)) {
    const found = list.find((t) => t.id === taskId);
    if (found) return found;
  }
  return undefined;
}

/** Apply a shallow optimistic change locally; returns a revert function. */
function optimisticPatch(taskId: number, partial: Partial<TaskListItem>): () => void {
  let snapAreaId: number | undefined;
  let snapTask: TaskListItem | undefined;

  useTasksStore.setState((s) => {
    const tba = { ...s.tasksByArea };
    for (const [aid, list] of Object.entries(tba)) {
      const i = list.findIndex((t) => t.id === taskId);
      if (i >= 0) {
        snapAreaId = Number(aid);
        snapTask = list[i];
        const copy = [...list];
        copy[i] = { ...list[i], ...partial };
        tba[Number(aid)] = copy;
        break;
      }
    }
    return { tasksByArea: tba };
  });

  const ds = useTaskDetailStore.getState();
  const prevDetail = ds.openId === taskId ? ds.task : null;
  if (prevDetail) useTaskDetailStore.setState({ task: { ...prevDetail, ...partial } });

  return () => {
    if (snapTask && snapAreaId !== undefined) {
      useTasksStore.setState((s) => {
        const tba = { ...s.tasksByArea };
        const list = tba[snapAreaId!];
        if (list) {
          const i = list.findIndex((t) => t.id === taskId);
          if (i >= 0) {
            const copy = [...list];
            copy[i] = snapTask!;
            tba[snapAreaId!] = copy;
          }
        }
        return { tasksByArea: tba };
      });
    }
    // Only revert the panel if it's STILL showing this task — a failed save can
    // land after the user has opened another task, and writing the old task back
    // would replace what they're looking at with a stale, unrelated record.
    if (prevDetail && useTaskDetailStore.getState().openId === taskId) {
      // prevDetail is the PRE-write snapshot; a teammate's change may have landed
      // meanwhile, so show it immediately then re-read the authoritative task so
      // the panel settles on the server's version (mirrors the board refetch).
      useTaskDetailStore.setState({ task: prevDetail });
      void useTaskDetailStore.getState().refresh();
    }
    // The snapshot is from BEFORE our write; a teammate's update may have landed
    // in between, and restoring the snapshot would discard it. Re-read the truth
    // in the background so the board settles on the server's version, not ours.
    if (snapAreaId !== undefined) {
      const st = useTasksStore.getState();
      void st.loadTasks(snapAreaId, st.loadedClosed[snapAreaId] ?? false, true);
    }
  };
}

/** Reconcile local state to the server's authoritative task. */
function applyServerTask(updated: TaskDetail, prevAreaId?: number): void {
  const item = toListItem(updated);
  const showCompleted = useFilterStore.getState().showCompleted;
  const nowClosed = CLOSED.has(updated.state);

  useTasksStore.setState((s) => {
    const tba = { ...s.tasksByArea };
    // Drop from previous area if it moved.
    if (prevAreaId !== undefined && prevAreaId !== updated.areaId && tba[prevAreaId]) {
      tba[prevAreaId] = tba[prevAreaId].filter((t) => t.id !== updated.id);
    }
    const list = tba[updated.areaId];
    if (list) {
      const i = list.findIndex((t) => t.id === updated.id);
      if (nowClosed && !showCompleted) {
        // It just closed and we're hiding completed — remove it from the board.
        if (i >= 0) tba[updated.areaId] = list.filter((t) => t.id !== updated.id);
      } else if (i >= 0) {
        const copy = [...list];
        copy[i] = item;
        tba[updated.areaId] = copy;
      } else if (!item.parentId) {
        // boards/tables list top-level tasks only — never inject sub-tasks
        tba[updated.areaId] = [item, ...list];
      }
    }
    // Stamp the same freshness marker a live socket event would. Without it, a
    // background refetch that started BEFORE this local write lands afterwards and
    // resurrects the pre-write row — a completed card reappears, or a moved card
    // pops back into its old area.
    const now = Date.now();
    const liveAt = { ...s.liveAt };
    for (const aid of [updated.areaId, prevAreaId]) if (aid !== undefined) liveAt[aid] = now;
    return { tasksByArea: tba, liveAt };
  });

  if (useTaskDetailStore.getState().openId === updated.id) {
    useTaskDetailStore.setState({ task: updated });
  }
}

// Nudge the creator at most once per ~25s so quick planning bursts don't spam.
let lastUnassignedNudge = 0;

/** Create a new task (in a given area/stage) and insert it into the board. */
export async function createTask(input: CreateTaskInput): Promise<TaskDetail | null> {
  try {
    const { task } = await api.post<{ task: TaskDetail }>('/tasks', input);
    applyServerTask(task);
    // Unassigned top-level task → gentle reminder (the lead is also pinged
    // server-side). Throttled so rapid board quick-adds don't nag.
    const noAssignee = !input.assigneeIds || input.assigneeIds.length === 0;
    if (noAssignee && input.parentId === undefined && task.assignees.length === 0) {
      const now = Date.now();
      if (now - lastUnassignedNudge > 25_000) {
        lastUnassignedNudge = now;
        useToastStore.getState().show('Added — no assignee yet, so nobody’s notified. Assign someone.', 'info', {
          label: 'Assign',
          run: () => void useTaskDetailStore.getState().open(task.id),
        });
      }
    }
    return task;
  } catch (err) {
    useToastStore.getState().show(err instanceof Error ? err.message : 'Could not create task.', 'error');
    return null;
  }
}

/** Move a task to another stage (optimistic). Returns true on success. */
export async function moveStage(taskId: number, stageId: number): Promise<boolean> {
  const prevAreaId = areaOf(taskId);
  // Capture pre-move state/stage so an auto-complete (drop into a Done column) can be
  // announced + undone instead of the card silently vanishing from the board.
  const before = listItemOf(taskId);
  const revert = optimisticPatch(taskId, { stageId });
  try {
    const { task } = await api.post<{ task: TaskDetail }>(`/tasks/${taskId}/move`, { stageId });
    applyServerTask(task, prevAreaId);
    if (before && !CLOSED.has(before.state) && CLOSED.has(task.state)) {
      const verb = task.state === '1_canceled' ? 'cancelled' : 'completed';
      const label = task.name.length > 40 ? `${task.name.slice(0, 40)}…` : task.name;
      useToastStore.getState().show(`“${label}” marked ${verb}.`, 'info', {
        label: 'Undo',
        undo: true,
        // Send it back to the column it came from; the server re-opens a task dragged
        // out of a terminal column, so this restores BOTH the column and the state.
        run: () => { void moveStage(taskId, before.stageId); },
      }, { protect: true });
    }
    return true;
  } catch (err) {
    revert();
    useToastStore.getState().show(err instanceof Error ? err.message : 'Could not move task.', 'error');
    return false;
  }
}

/** Close a task (done or cancelled) with an Undo toast.
 *
 *  WHY IT'S GENERIC: closing a task REMOVES its row from the board/table (completed
 *  work is hidden by default), so the same gesture done two ways must not behave two
 *  ways. The ✓ buttons routed through here and got a protected Undo, but the State
 *  dropdown called patchTask directly — so picking "Done"/"Cancelled" from the menu
 *  made the row silently vanish with no toast and no way back ("did that save? where
 *  did my task go?"). Every closing transition now lands here. */
export async function closeWithUndo(
  taskId: number,
  name: string,
  prevState: TaskListItem['state'],
  nextState: '1_done' | '1_canceled' = '1_done',
  onUndo?: () => void,
  /** The reason that was on the card pre-close (panel callers know it; board
   *  rows don't carry it). Undo restores it — any state write without one
   *  clears the column, and undoing a mis-tap must not eat a Stuck reason. */
  prevReason?: string | null,
): Promise<boolean> {
  const ok = await patchTask(taskId, { state: nextState }, { state: nextState });
  if (ok) {
    const label = name.length > 40 ? `${name.slice(0, 40)}…` : name;
    useToastStore.getState().show(`"${label}" ${nextState === '1_canceled' ? 'cancelled' : 'completed'}.`, 'info', {
      label: 'Undo',
      undo: true,
      run: () => {
        // suppressNotify: undoing a mis-tap must not fire a spurious "Approved"
        // ping or trip content-team auto-advance.
        void patchTask(taskId, { state: prevState, stateReason: prevReason ?? undefined, suppressNotify: true } as never, { state: prevState });
        onUndo?.();
      },
    }, { protect: true });
  }
  return ok;
}

/** Mark a task done with an Undo toast — the ✓ is one tap on phones, so a brushed
 *  thumb must never silently close real work. Thin wrapper over closeWithUndo. */
export async function completeWithUndo(taskId: number, name: string, prevState: TaskListItem['state'], onUndo?: () => void): Promise<boolean> {
  return closeWithUndo(taskId, name, prevState, '1_done', onUndo);
}

/** Generic field patch. Pass `optimistic` to apply instantly before the round-trip. */
export async function patchTask(
  taskId: number,
  input: UpdateTaskInput,
  optimistic?: Partial<TaskListItem>,
): Promise<boolean> {
  const prevAreaId = areaOf(taskId);
  const revert = optimistic ? optimisticPatch(taskId, optimistic) : null;
  try {
    const { task } = await api.patch<{ task: TaskDetail }>(`/tasks/${taskId}`, input);
    applyServerTask(task, prevAreaId);
    return true;
  } catch (err) {
    revert?.();
    useToastStore.getState().show(err instanceof Error ? err.message : 'Could not save change.', 'error');
    return false;
  }
}
